class ProductConfiguratorGroup extends HTMLElement {
  connectedCallback() {
    this.setupConditionalElements();
  }

  setupConditionalElements() {
    // Find all elements with show-if attributes
    const conditionalElements = this.querySelectorAll('[show-if]');
    
    conditionalElements.forEach(element => {
      const showIf = element.getAttribute('show-if');
      const inputId = showIf.split(':')[0]; // Get the input ID before the colon
      const targetInput = document.getElementById(inputId);
            
      if (targetInput) {
        const inputName = targetInput.name;
        
        // Find all inputs with the same name (radio group)
        const radioGroup = document.querySelectorAll(`input[name="${inputName}"]`);
        
        // Function to check visibility
        const updateVisibility = () => {
          if (targetInput.checked) {
            element.removeAttribute('hidden');
            // Restore required attributes
            this.restoreRequiredAttributes(element);
          } else {
            element.setAttribute('hidden', 'true');
            // Store and remove required attributes before resetting
            this.storeAndRemoveRequiredAttributes(element);
            // Reset all form fields within the hidden element
            this.resetFormFields(element);
          }
        };
        
        // Listen to changes on all inputs in the radio group
        radioGroup.forEach(radioInput => {
          radioInput.addEventListener('change', () => {
            updateVisibility();
          });
        });
        
        // Set initial state
        updateVisibility();
      }
    });
  }

  resetFormFields(element) {
    // Find all form inputs within the element
    const inputs = element.querySelectorAll('input, textarea, select');
    
    inputs.forEach(input => {
      // The date picker renders from its input, so let it know it was cleared
      if (input.hasAttribute('data-date-input')) {
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      switch (input.type) {
        case 'text':
        case 'email':
        case 'tel':
        case 'url':
        case 'password':
        case 'search':
          input.value = '';
          break;
        case 'textarea':
          input.value = '';
          break;
        case 'checkbox':
        case 'radio':
          input.checked = false;
          break;
        case 'select-one':
        case 'select-multiple':
          input.selectedIndex = -1;
          break;
        default:
          // Handle textarea separately since it doesn't have a type
          if (input.tagName.toLowerCase() === 'textarea') {
            input.value = '';
          }
          break;
      }
    });
  }

  storeAndRemoveRequiredAttributes(element) {
    // Find all required inputs within the element
    const requiredInputs = element.querySelectorAll('input[required], textarea[required], select[required]');
    
    requiredInputs.forEach(input => {
      // Store the fact that this input was required
      input.dataset.wasRequired = 'true';
      // Remove the required attribute to prevent validation errors
      input.removeAttribute('required');
    });
  }

  restoreRequiredAttributes(element) {
    // Find all inputs that were previously required
    const previouslyRequiredInputs = element.querySelectorAll('input[data-was-required="true"], textarea[data-was-required="true"], select[data-was-required="true"]');
    
    previouslyRequiredInputs.forEach(input => {
      // Restore the required attribute
      input.setAttribute('required', '');
      // Clean up the data attribute
      input.removeAttribute('data-was-required');
    });
  }
}

class ProductConfiguratorCalendar extends HTMLElement {
  constructor() {
    super();
    this.calendar = null;
  }

  connectedCallback() {
    // The modal is moved to <body> when it opens, which reconnects this
    // element - the calendar must only ever be initialized once
    if (this.calendar) return;

    // Wait for VanillaCalendarPro to be loaded
    if (window.VanillaCalendarPro) {
      this.initializeCalendar();
    } else {
      // Wait for the library to load
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeCalendar();
      });
    }
  }

  initializeCalendar() {
    const { Calendar } = window.VanillaCalendarPro;

    // Function to format date to UK format (dd/mm/yyyy)
    function formatDateToUK(dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }

    // Announce the current selection - the date picker decides when to commit it
    const announceSelection = (selectedDates, selectedTime) => {
      const date = selectedDates && selectedDates.length > 0 ? selectedDates[0] : '';
      let value = '';

      if (date) {
        value = formatDateToUK(date);

        if (selectedTime) {
          value += ` ${selectedTime}`;
        }
      }

      this.dispatchEvent(new CustomEvent(ProductConfiguratorCalendar.CHANGE_EVENT, {
        bubbles: true,
        detail: { date: date, time: selectedTime || '', value: value }
      }));
    };

    const dateOptions = this.getDateOptions();

    const options = {
      selectedTheme: 'light',
      inputMode: false,
      selectionTimeMode: this.getAttribute('selection-time-mode') === 'false' ? false : 12,
      timeMinHour: 9,
      timeMaxHour: 17,
      timeControls: 'all',
      onClickDate(self) {
        announceSelection(self.context.selectedDates, self.context.selectedTime);
      },
      onChangeTime(self) {
        announceSelection(self.context.selectedDates, self.context.selectedTime);
      },
      ...dateOptions
    };

    // Create and initialize calendar
    this.dateMin = dateOptions.dateMin;
    this.calendar = new Calendar(this, options);
    this.calendar.init();
  }

  /**
   * Works out which dates can be chosen right now from this product's rules,
   * using the shared delivery date logic (assets/delivery-dates.js)
   */
  getDateOptions() {
    const list = (name) => (this.getAttribute(name) || '').split(',').map((value) => value.trim()).filter(Boolean);

    const { dates, earliest, latest } = window.DeliveryDates.availableDates([{
      leadTimeDays: this.getAttribute('lead-time-days'),
      businessDaysOnly: this.getAttribute('business-days-only'),
      blackoutDays: list('blackout-days'),
      blackoutDayExceptions: list('blackout-day-exception-dates'),
      blackoutDates: list('blackout-dates'),
      inclusionDates: list('inclusion-dates')
    }], this.getAttribute('cutoff-time'));

    const dateOptions = {
      dateMin: earliest,
      dateMax: latest,
      disableAllDates: true,
      enableDates: dates
    };

    // Open on the first available date so the picker doesn't land on an empty month
    if (dates.length > 0) {
      const [year, month] = dates[0].split('-');
      dateOptions.selectedYear = parseInt(year, 10);
      dateOptions.selectedMonth = parseInt(month, 10) - 1;
    }

    return dateOptions;
  }

  /**
   * Re-applies the date rules when the earliest date has moved since the
   * calendar was built (the cutoff or midnight passed with the page open)
   */
  refreshDates() {
    if (!this.calendar) return;

    const dateOptions = this.getDateOptions();
    if (dateOptions.dateMin === this.dateMin) return;

    this.dateMin = dateOptions.dateMin;
    this.calendar.set(dateOptions);
  }

  /**
   * Restores the calendar to a given selection, used when a picker is
   * re-opened or when a pending selection is abandoned
   */
  setSelection(date, time) {
    if (!this.calendar) return;

    this.calendar.set({
      selectedDates: date ? [date] : [],
      selectedTime: time || undefined
    });
  }
}

ProductConfiguratorCalendar.CHANGE_EVENT = 'configurator:date-change';

/**
 * Date picker for the configurator - a "Choose Date" button that opens the
 * calendar in a modal, and a summary with an "Edit" button once a date is set
 */
class ProductConfiguratorDatePicker extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-date-input]');
    this.trigger = this.querySelector('[data-date-trigger]');
    this.summary = this.querySelector('[data-date-summary]');
    this.value = this.querySelector('[data-date-value]');
    this.confirmButton = this.querySelector('[data-date-confirm]');
    this.calendar = this.querySelector('product-configurator-calendar');
    this.modalId = this.getAttribute('modal-id');

    if (!this.input || !this.calendar) return;

    // The modal is moved to the end of <body> while open, so listeners are
    // bound to the elements themselves rather than relying on bubbling
    this.calendar.addEventListener(ProductConfiguratorCalendar.CHANGE_EVENT, this.onDateChange.bind(this));
    this.confirmButton?.addEventListener('click', this.onConfirm.bind(this));
    this.input.addEventListener('change', this.render.bind(this));

    this.modal?.addEventListener('toggle', this.onModalToggle.bind(this));

    this.render();
  }

  get modal() {
    return this.modalId ? document.getElementById(this.modalId) : null;
  }

  onDateChange(event) {
    this.pending = event.detail;

    if (this.confirmButton) {
      this.confirmButton.disabled = !this.pending.date;
    }
  }

  onConfirm() {
    if (!this.pending || !this.pending.date) return;

    this.input.value = this.pending.value;
    this.input.dataset.date = this.pending.date;
    this.input.dataset.time = this.pending.time;
    this.input.setCustomValidity('');

    this.render();
    this.modal?.hide();
  }

  onModalToggle() {
    if (this.modal?.open) {
      this.onModalOpen();
      return;
    }

    // Closing without confirming discards the pending selection
    this.pending = null;
    this.calendar.setSelection(this.input.dataset.date, this.input.dataset.time);

    if (this.confirmButton) {
      this.confirmButton.disabled = !this.input.dataset.date;
    }
  }

  onModalOpen() {
    this.calendar.refreshDates();

    // Drop a confirmed date that the cutoff has overtaken since it was chosen
    const date = this.input.dataset.date;
    if (!date || !this.calendar.dateMin || date >= this.calendar.dateMin) return;

    this.input.value = '';
    this.render();
    this.calendar.setSelection();

    if (this.confirmButton) {
      this.confirmButton.disabled = true;
    }
  }

  render() {
    const hasValue = this.input.value !== '';

    if (!hasValue) {
      delete this.input.dataset.date;
      delete this.input.dataset.time;
    }

    this.trigger.hidden = hasValue;
    this.summary.hidden = !hasValue;

    if (this.value) {
      this.value.textContent = hasValue ? this.format(this.input.value) : '';
    }
  }

  /**
   * Turns "25/12/2026 10:00" into "Fri, 25 Dec 2026, 10:00"
   */
  format(value) {
    const [date, ...rest] = value.split(' ');
    const time = rest.join(' ');
    const [day, month, year] = date.split('/');
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);

    if (isNaN(parsed)) return value;

    const formatted = parsed.toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });

    return time ? `${formatted}, ${time}` : formatted;
  }
}

/**
 * Character counter for textarea inputs
 * Automatically updates character count display when used with is="character-count"
 */
class CharacterCount extends HTMLElement {
  connectedCallback() {
    this.setupCharacterCounting();
  }

  /**
   * Sets up character counting for associated textarea
   */
  setupCharacterCounting() {
    // Find the associated textarea - look for sibling or parent's textarea
    const textarea = this.findAssociatedTextarea();
    
    if (!textarea) {
      console.warn('CharacterCount: No associated textarea found');
      return;
    }

    // Get max length from data attribute or textarea's maxlength attribute
    const maxLength = this.getMaxLength(textarea);
    
    if (!maxLength) {
      console.warn('CharacterCount: No max length specified');
      return;
    }

    // Update count on input
    textarea.addEventListener('input', () => {
      this.updateCount(textarea, maxLength);
    });

    // Set initial count
    this.updateCount(textarea, maxLength);
  }

  /**
   * Finds the textarea associated with this character counter using data-field-id
   * @returns {HTMLTextAreaElement|null} The associated textarea
   */
  findAssociatedTextarea() {
    const fieldId = this.getAttribute('data-field-id');
    
    if (!fieldId) {
      console.warn('CharacterCount: No data-field-id attribute specified');
      return null;
    }

    const textarea = document.getElementById(fieldId);
    
    if (!textarea || textarea.tagName !== 'TEXTAREA') {
      console.warn(`CharacterCount: No textarea found with id "${fieldId}"`);
      return null;
    }

    return textarea;
  }

  /**
   * Gets the maximum character length from data attribute or textarea maxlength
   * @param {HTMLTextAreaElement} textarea The textarea element
   * @returns {number|null} The maximum length
   */
  getMaxLength(textarea) {
    // Check for data-max-length attribute on this element
    let maxLength = this.getAttribute('data-max-length');
    
    // Check for data-max-length on the textarea
    if (!maxLength) {
      maxLength = textarea.getAttribute('data-max-length');
    }
    
    // Fall back to maxlength attribute on textarea
    if (!maxLength) {
      maxLength = textarea.getAttribute('maxlength');
    }

    return maxLength ? parseInt(maxLength, 10) : null;
  }

  /**
   * Updates the character count display
   * @param {HTMLTextAreaElement} textarea The textarea element
   * @param {number} maxLength The maximum character length
   */
  updateCount(textarea, maxLength) {
    const currentLength = textarea.value.length;
    this.querySelector('span').textContent = currentLength.toString();
    
    // Add visual indication if approaching or exceeding limit
    this.classList.remove('character-count--warning', 'character-count--error');
    
    if (currentLength >= maxLength) {
      this.classList.add('character-count--error');
    } else if (currentLength >= maxLength * 0.8) {
      this.classList.add('character-count--warning');
    }
  }
}

/**
 * Repeats a block of guest fields once per unit of the product quantity.
 * Expects a <template data-guest-template> whose markup contains the
 * __GUEST__ placeholder, and a [data-guest-list] container. Existing guest
 * groups are kept (so typed values survive) and only the tail is added/removed.
 */
class ProductConfiguratorGuests extends HTMLElement {
  static PLACEHOLDER = /__GUEST__/g;
  static MAX_GUESTS = 100;

  connectedCallback() {
    this.template = this.querySelector('template[data-guest-template]');
    this.list = this.querySelector('[data-guest-list]');
    this.minGuests = Math.max(1, parseInt(this.getAttribute('min-guests'), 10) || 1);

    if (!this.template || !this.list) return;

    this.onQuantityChange = this.onQuantityChange.bind(this);
    // Delegate so we keep working if the quantity selector is re-rendered.
    document.addEventListener('change', this.onQuantityChange);
    document.addEventListener('input', this.onQuantityChange);

    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onQuantityChange);
    document.removeEventListener('input', this.onQuantityChange);
  }

  get quantityInput() {
    const id = this.getAttribute('quantity-input-id');
    return (id && document.getElementById(id)) || null;
  }

  get quantity() {
    const input = this.quantityInput;
    const value = input ? parseInt(input.value, 10) : NaN;
    if (Number.isNaN(value)) return this.minGuests;
    return Math.min(ProductConfiguratorGuests.MAX_GUESTS, Math.max(this.minGuests, value));
  }

  onQuantityChange(event) {
    const input = this.quantityInput;
    if (input && event.target === input) this.sync();
  }

  sync() {
    const target = this.quantity;
    const groups = () => this.list.querySelectorAll(':scope > [data-guest-index]');

    while (groups().length > target) {
      const last = groups()[groups().length - 1];
      last.remove();
    }

    let count = groups().length;
    while (count < target) {
      count++;
      this.list.insertAdjacentHTML('beforeend', this.render(count));
    }
  }

  render(index) {
    return this.template.innerHTML.replace(ProductConfiguratorGuests.PLACEHOLDER, String(index));
  }
}

// Register the web components
if (!customElements.get('product-configurator-group')) {
  customElements.define('product-configurator-group', ProductConfiguratorGroup);
}

if (!customElements.get('product-configurator-calendar')) {
  customElements.define('product-configurator-calendar', ProductConfiguratorCalendar);
}

if (!customElements.get('product-configurator-date-picker')) {
  customElements.define('product-configurator-date-picker', ProductConfiguratorDatePicker);
}

// Register character count element with is="" syntax
if (!customElements.get('character-count')) {
  customElements.define('character-count', CharacterCount);
}

if (!customElements.get('product-configurator-guests')) {
  customElements.define('product-configurator-guests', ProductConfiguratorGuests);
}
